<script lang="ts">
  import { app, type SearchGroup, type SearchHit } from './appState.svelte';

  /** M5 spec §3.2: expandable library search — 🔍 grows into an input, 300ms
      debounce → app.search (server FTS + local grouping), grouped dropdown.
      Tapping a hit hands its OCR boxes to the Editor via pendingSearchHighlight
      and routes to the page. Needs sync (server-side search): when disabled
      the input is disabled with a hint. */

  const DEBOUNCE_MS = 300;

  let expanded = $state(false);
  let query = $state('');
  let groups = $state<SearchGroup[] | null>(null); // null = nothing searched yet
  let searching = $state(false);
  let error = $state<string | null>(null);
  let inputEl: HTMLInputElement | undefined = $state();
  let rootEl: HTMLDivElement | undefined = $state();
  let debounce: ReturnType<typeof setTimeout> | null = null;
  let seq = 0; // stale-response guard: only the latest search may land

  const syncOff = $derived(app.syncStatus === 'disabled');

  function expand() {
    expanded = true;
    // Focus after the input mounts.
    setTimeout(() => inputEl?.focus(), 0);
  }

  function collapse() {
    if (debounce !== null) { clearTimeout(debounce); debounce = null; }
    seq++; // invalidate any in-flight search
    expanded = false;
    query = '';
    groups = null;
    searching = false;
    error = null;
  }

  function onInput() {
    error = null;
    if (debounce !== null) clearTimeout(debounce);
    if (!query.trim()) { groups = null; searching = false; return; }
    debounce = setTimeout(() => { debounce = null; void run(); }, DEBOUNCE_MS);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') { collapse(); return; }
    if (e.key === 'Enter') {
      if (debounce !== null) { clearTimeout(debounce); debounce = null; }
      void run();
    }
  }

  /**
   * Collapse when focus leaves the search bar for another focusable element
   * (e.g. tabbing to a header button). Deferred a tick: expanding removes the
   * focused 🔍 button, which fires a focusout BEFORE the input gets focus —
   * the deferred check sees focus back inside and keeps the bar open. Focus
   * falling to <body> is IGNORED here (it happens on that button removal, and
   * whenever the input is disabled — sync off — and can't hold focus);
   * clicking outside is covered by the document pointerdown below instead.
   */
  function onFocusOut() {
    setTimeout(() => {
      const active = document.activeElement;
      if (!expanded || !rootEl || active === document.body || active === null) return;
      if (!rootEl.contains(active)) collapse();
    }, 0);
  }

  /** Outside tap/click collapses — the blur path can't (see onFocusOut). */
  function onDocPointerDown(e: PointerEvent) {
    if (!expanded) return;
    if (e.target instanceof Node && rootEl?.contains(e.target)) return;
    collapse();
  }

  async function run() {
    const q = query.trim();
    if (!q || syncOff) return;
    const mySeq = ++seq;
    searching = true;
    try {
      const out = await app.search(q);
      if (mySeq !== seq) return; // superseded (or collapsed)
      groups = out;
      error = null;
    } catch {
      if (mySeq !== seq) return;
      groups = null;
      error = 'Search failed — check your connection.';
    } finally {
      if (mySeq === seq) searching = false;
    }
  }

  /** Escape HTML, then turn the snippet's literal `**…**` markers into <b>. */
  function renderSnippet(snippet: string): string {
    const escaped = snippet
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    return escaped.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  }

  function open(hit: SearchHit) {
    if (!hit.notebookId) return; // unknown locally — nowhere to route
    app.pendingSearchHighlight =
      hit.kind === 'ocr' && hit.boxes && hit.boxes.length > 0
        ? { pageId: hit.pageId, boxes: hit.boxes }
        : null;
    const target = { notebookId: hit.notebookId, pageId: hit.pageId };
    collapse();
    app.route = { screen: 'editor', ...target };
  }

  const hitCount = $derived(groups?.reduce((n, g) => n + g.hits.length, 0) ?? 0);
</script>

<svelte:document onpointerdown={onDocPointerDown} />

<div class="search-bar" bind:this={rootEl} onfocusout={onFocusOut}>
  {#if !expanded}
    <button class="search-toggle" title="Search" aria-label="Search" onclick={expand}>🔍</button>
  {:else}
    <div class="search-open">
      <input
        bind:this={inputEl}
        bind:value={query}
        type="search"
        placeholder={syncOff ? 'Enable sync to search' : 'Search notes…'}
        disabled={syncOff}
        aria-label="Search notes"
        oninput={onInput}
        onkeydown={onKeydown}
      />
      {#if syncOff}
        <div class="search-dropdown hint">Search runs on the server — enable sync in Settings first.</div>
      {:else if error}
        <div class="search-dropdown hint">{error}</div>
      {:else if groups !== null}
        <div class="search-dropdown search-results">
          {#if hitCount === 0}
            <div class="hint-row">{searching ? 'Searching…' : 'No matches.'}</div>
          {:else}
            {#each groups as group (group.notebookId ?? '__other')}
              <div class="group-title">{group.title}</div>
              {#each group.hits as hit (hit.pageId + hit.kind)}
                <button
                  class="result-row"
                  class:ocr={hit.kind === 'ocr'}
                  disabled={!hit.notebookId}
                  onclick={() => open(hit)}
                >
                  <span class="badge" title={hit.kind === 'ocr' ? 'Handwriting' : 'Text'}>{hit.kind === 'ocr' ? '✎' : '⌨'}</span>
                  <span class="row-main">
                    {#if hit.pageNumber !== null}<span class="page-no">p. {hit.pageNumber}</span>{/if}
                    <!-- eslint-disable-next-line svelte/no-at-html-tags — renderSnippet escapes first -->
                    <span class="snippet">{@html renderSnippet(hit.snippet)}</span>
                  </span>
                </button>
              {/each}
            {/each}
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .search-bar { position: relative; }
  .search-toggle { border: none; cursor: pointer; border-radius: var(--radius-panel); background: var(--surface); color: var(--ink-primary); padding: 10px 16px; min-height: 44px; }
  .search-open input { width: min(320px, 42vw); min-height: 44px; padding: 0 12px; border: 1px solid var(--hairline); border-radius: var(--radius-panel); background: var(--surface); color: var(--ink-primary); font: inherit; }
  .search-open input:disabled { opacity: 0.6; }
  .search-dropdown { position: absolute; top: calc(100% + 6px); right: 0; width: min(420px, 86vw); max-height: 60vh; overflow-y: auto; background: var(--surface); border-radius: var(--radius-panel); box-shadow: var(--shadow-soft); z-index: 20; padding: 6px; text-align: left; }
  .search-dropdown.hint, .hint-row { padding: 12px 14px; font-size: 13px; opacity: 0.7; }
  .group-title { padding: 8px 10px 4px; font-size: 12px; font-weight: 600; opacity: 0.6; text-transform: none; }
  .result-row { display: flex; align-items: flex-start; gap: 8px; width: 100%; border: none; background: transparent; color: var(--ink-primary); text-align: left; padding: 8px 10px; border-radius: 8px; cursor: pointer; font: inherit; font-size: 13px; min-height: 44px; }
  .result-row:hover:not(:disabled), .result-row:focus-visible { background: var(--paper); }
  .result-row:disabled { opacity: 0.55; cursor: default; }
  .badge { flex: none; width: 20px; text-align: center; opacity: 0.7; }
  .row-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .page-no { font-size: 11px; opacity: 0.6; }
  .snippet { overflow-wrap: anywhere; }
  .snippet :global(b) { color: var(--accent); }
</style>
